import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Icon } from '@mui/material';
import FilterList from '@mui/icons-material/FilterList';
import { FakeText, Typography, Card } from '@totalsoft/rocket-ui';
import { FULL_GENERIC_FILTER_TEMPLATE } from 'features/genericFilterTemplates/edit/queries';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { UPDATE_FINANCIAL_PRODUCT_WARRANTY_FILTERS } from '../../mutations';
import { FINANCIAL_PRODUCT_WARRANTY_FILTER_UPDATED } from '../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import useGenericFilterData, { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';

const FinancialProductWarrantyEditFiltersContainer = ({ canWrite, leasingFinancialProductXWarrantyId, templateId }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const isNewWarranty = useMemo(() => !leasingFinancialProductXWarrantyId, [leasingFinancialProductXWarrantyId]);

  const [loaded, setLoaded] = useState(false);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultTemplate);

  // #region Data fetching

  const { templates, parameters, operators, loadValues, loadPagedValues, loadMissingParamters, loadTemplate } = useGenericFilterData({
    context: GenericFilterContext.FinancialProduct,
    withTemplateSelection: true
  });

  const { loading } = useQuery(FULL_GENERIC_FILTER_TEMPLATE, {
    skip: !templateId,
    variables: { id: templateId },
    onCompleted: data => {
      const generalData = data.genericFilterTemplateGeneralData;
      const templateData = {
        withTemplate: Boolean(generalData.isTemplate),
        templateId: generalData.id,
        disabled: !canWrite,
        template: {
          phrase: data.genericFilterTemplateDetails.phrase,
          conditions: data.genericFilterTemplateDetails.conditions || []
        }
      };
      if (templateData.withTemplate) loadMissingParamters(templateData.template.conditions);
      resetLens(templateData);
      setInitialData(templateData);
      setLoaded(true);
    }
  });

  // #endregion

  // #region Save

  const [mutateConditions] = useMutation(UPDATE_FINANCIAL_PRODUCT_WARRANTY_FILTERS, {
    onError: _ => {
      (lens.disabled |> set)(false);
    }
  });

  const handleSave = data => {
    (lens.disabled |> set)(true);

    const input = { leasingFinancialProductXWarrantyId, ...data };

    mutateConditions({
      variables: { input },
      errorHandlingParams: {}
    });
  };

  useSubscription(FINANCIAL_PRODUCT_WARRANTY_FILTER_UPDATED, {
    skip: !leasingFinancialProductXWarrantyId,
    variables: { leasingFinancialProductXWarrantyId },
    onData: ({ client }) => {
      const currentData = lens |> get;
      const newData = {
        ...currentData,
        disabled: false,
        template: { ...currentData.template, conditions: currentData.template.conditions.map(x => ({ ...x, editing: false })) }
      };
      resetLens(newData);
      setInitialData(newData);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterTemplateDetails' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterTemplateGeneralData' });
      client.cache.evict({
        id: `FinancialProductWarrantyEditItem:{"leasingFinancialProductXWarrantyId":${leasingFinancialProductXWarrantyId}}`
      });
      client.cache.gc();
      addToast(t('FinancialProduct.Details.Warranties.FiltersSaved'), 'success');
    }
  });

  // #endregion

  const showLoading = useMemo(() => loading && !loaded, [loading, loaded]);

  return (
    <Card icon={FilterList} iconColor='primary' title={t('FinancialProduct.Details.Warranties.Filters')}>
      <Grid container flexDirection='column' gap={2} p={2}>
        {isNewWarranty && (
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('FinancialProduct.Details.Warranties.Warning')}</Typography>
          </Box>
        )}
        {!isNewWarranty && showLoading && (
          <Grid item>
            <FakeText lines={5} />
          </Grid>
        )}
        {!isNewWarranty && !showLoading && (
          <GenericFilterComposer
            lens={lens}
            dirtyInfo={dirtyInfo}
            resetLens={resetLens}
            templates={templates}
            parameters={parameters}
            operators={operators}
            loadValues={loadValues}
            loadPagedValues={loadPagedValues}
            loadTemplate={loadTemplate}
            onSave={handleSave}
            conditionsViewMode='table'
            withTemplateGeneralData
            withTemplateSelection
            withSlottedAddConditionAction
            forwardProps={{ container: { mt: -1 } }}
          />
        )}
      </Grid>
    </Card>
  );
};

FinancialProductWarrantyEditFiltersContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  leasingFinancialProductXWarrantyId: PropTypes.number,
  templateId: PropTypes.number
};

export default FinancialProductWarrantyEditFiltersContainer;
